import type { StatsGranularity } from '@gl/shared';

export type RangePreset = '7d' | '30d' | 'month' | 'lastMonth' | 'custom';

export const PRESET_LABELS: Record<RangePreset, string> = {
  '7d': '7 días',
  '30d': '30 días',
  month: 'Este mes',
  lastMonth: 'Mes pasado',
  custom: 'Personalizado',
};

export const GRANULARITY_LABELS: Record<StatsGranularity, string> = {
  day: 'Por día',
  week: 'Por semana',
  month: 'Por mes',
};

/** Fecha de caja actual ("YYYY-MM-DD") en la zona del local, igual que la API. */
export function currentBusinessDate(timezone: string, startHour: number, now = new Date()): string {
  const shifted = new Date(now.getTime() - startHour * 3_600_000);
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(shifted);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function presetRange(preset: Exclude<RangePreset, 'custom'>, today: string): { from: string; to: string } {
  switch (preset) {
    case '7d':
      return { from: addDays(today, -6), to: today };
    case '30d':
      return { from: addDays(today, -29), to: today };
    case 'month':
      return { from: `${today.slice(0, 7)}-01`, to: today };
    case 'lastMonth': {
      const firstThis = new Date(`${today.slice(0, 7)}-01T00:00:00Z`);
      const lastPrev = addDays(firstThis.toISOString().slice(0, 10), -1);
      return { from: `${lastPrev.slice(0, 7)}-01`, to: lastPrev };
    }
  }
}

export function rangeDays(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
}

const fmt = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('es-AR', { timeZone: 'UTC', ...opts });
const dayFmt = fmt({ day: '2-digit', month: '2-digit' });
const monthFmt = fmt({ month: 'short', year: '2-digit' });
const longFmt = fmt({ weekday: 'short', day: 'numeric', month: 'short' });

export function bucketLabel(bucket: string, g: StatsGranularity): string {
  const d = new Date(`${bucket}T12:00:00Z`);
  if (g === 'month') return monthFmt.format(d);
  return dayFmt.format(d);
}

export function bucketTitle(bucket: string, g: StatsGranularity): string {
  const d = new Date(`${bucket}T12:00:00Z`);
  if (g === 'month') return fmt({ month: 'long', year: 'numeric' }).format(d);
  if (g === 'week') return `Semana del ${longFmt.format(d)}`;
  return longFmt.format(d);
}
